export type GhlMappingDiagnostics = {
  locations_count?: number;
  locations_with_facebook_id?: number;
  locations_with_google_id?: number;
  ghl_accounts_total?: number;
  ghl_accounts_mapped?: number;
  match_by_page_id?: number;
  match_by_name?: number;
};

export type AdsFeedImportResultData = {
  imported?: number;
  skipped?: number;
  unchanged?: number;
  location_corrected?: number;
  filtered_other_locations?: number;
  total_received?: number;
  scanned?: number;
  unmapped_accounts?: number;
  errors?: string[];
  mapping_diagnostics?: GhlMappingDiagnostics;
};

function formatMappingHint(d?: GhlMappingDiagnostics): string {
  if (!d) return "";
  const mapped = d.ghl_accounts_mapped ?? 0;
  const total = d.ghl_accounts_total ?? 0;
  const byName = d.match_by_name ?? 0;
  if (total === 0) return " No CRM social accounts returned — check API key scopes.";
  if (mapped === 0) {
    return (
      ` CRM returned ${total} account(s) but none matched app locations — ensure CRM account names include lot names` +
      ` (e.g. Wilson, Boiling Springs) or set Facebook Page IDs under Social mappings.`
    );
  }
  if (byName > 0 && (d.locations_with_facebook_id ?? 0) === 0) {
    return ` Matched ${mapped}/${total} CRM account(s) by display name (no Page IDs saved yet).`;
  }
  return ` Matched ${mapped}/${total} CRM account(s) to locations.`;
}

export function formatAdsFeedImportResult(d: AdsFeedImportResultData): string {
  const imported = d.imported ?? 0;
  const unchanged = typeof d.unchanged === "number" ? d.unchanged : 0;
  const corrected = typeof d.location_corrected === "number" ? d.location_corrected : 0;
  const filtered = typeof d.filtered_other_locations === "number" ? d.filtered_other_locations : 0;
  const scanned = typeof d.scanned === "number" ? d.scanned : 0;
  const unmapped = typeof d.unmapped_accounts === "number" ? d.unmapped_accounts : 0;
  const errList = Array.isArray(d.errors) ? d.errors.filter(Boolean) : [];
  const mapHint = formatMappingHint(d.mapping_diagnostics);

  if (imported === 0 && (d.total_received ?? 0) === 0 && corrected > 0) {
    const tail =
      unmapped > 0
        ? ` ${unmapped} post(s) still unmapped — add Page IDs in Social mappings or align CRM account names with lot names.`
        : "";
    return `Fixed ${corrected} location tag(s) on ${scanned || corrected} indexed CRM post(s).${tail}${mapHint}`;
  }

  if (imported === 0 && (d.total_received ?? 0) === 0 && corrected === 0 && scanned > 0) {
    return (
      `Scanned ${scanned} CRM post(s); none updated (${unmapped} unmapped).${mapHint}` +
      (errList.length ? ` Issues: ${errList.slice(0, 3).join("; ")}` : "")
    );
  }

  const parts = [
    imported > 0 ? `${imported} new or updated` : null,
    unchanged > 0 ? `${unchanged} already up to date` : null,
    corrected > 0 ? `${corrected} location tag(s) fixed` : null,
    (d.skipped ?? 0) > 0 ? `${d.skipped} skipped` : null,
  ].filter(Boolean);
  const summary = parts.length
    ? parts.join(", ")
    : imported === 0 && unchanged === 0
      ? `No changes${mapHint || " — nothing new to import"}`
      : "Sync complete";
  return (
    `${summary} (${d.total_received ?? 0} from CRM${filtered > 0 ? `, ${filtered} excluded for other locations` : ""}).${mapHint}` +
    (errList.length ? ` Issues: ${errList.slice(0, 5).join("; ")}` : "")
  );
}
